import { useContext, useEffect, useState } from "react";
import { t } from "i18next";
import { MagicStar, Trash } from "iconsax-react";
import DBButton from "../../../../component/global/DBButton";
import DBFieldBlock from "../../../../component/global/DBFieldBlock";
import DBInput from "../../../../component/global/DBInput";
import DBModal from "../../../../component/global/DBModal";
import DBSelect from "../../../../component/global/DBSelect";
import DBTextArea from "../../../../component/global/DBTextArea";
import { ToastContext } from "../../../../component/global/ToastContext";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import {
    SETTING_ITEMS_TYPE_AI_APPLY,
    SETTING_ITEMS_TYPE_AI_GENERATE,
} from "../../../../helperData/ApiAddress";
import { AiCoinHint, isInsufficientCoins, useAiCoinGate } from "../../../../features/ai-billing/AiCoinGate";

const INITIAL_ANSWERS = {
    business_description: "",
    offering_type: "mixed",
    offerings: "",
    customers: "",
    grouping_preference: "",
    exclusions: "",
    max_depth: 3,
};

const hasEmptyName = (nodes) =>
    nodes.some(
        (node) =>
            !node.name.trim() ||
            (node.children?.length > 0 && hasEmptyName(node.children))
    );

function TreeNode({ node, onChange, onDelete }) {
    const updateChild = (index, child) => {
        onChange({
            ...node,
            children: node.children.map((item, itemIndex) =>
                itemIndex === index ? child : item
            ),
        });
    };

    return (
        <li>
            <div className="ai-tree-node">
                <DBInput
                    value={node.name}
                    size="large"
                    onChange={(event) =>
                        onChange({ ...node, name: event.target.value })
                    }
                />
                <span className={`ai-type ai-type-${node.typ}`}>
                    {node.typ === 1
                        ? t("item.type.service")
                        : t("item.type.item")}
                </span>
                <button
                    type="button"
                    className="ai-tree-delete"
                    aria-label={t("ai.itemtype.remove")}
                    title={t("ai.itemtype.remove")}
                    onClick={onDelete}
                >
                    <Trash size={20} aria-hidden="true" />
                </button>
            </div>
            {node.children?.length > 0 && (
                <ul>
                    {node.children.map((child, index) => (
                        <TreeNode
                            key={index}
                            node={child}
                            onChange={(updatedChild) =>
                                updateChild(index, updatedChild)
                            }
                            onDelete={() =>
                                onChange({
                                    ...node,
                                    children: node.children.filter(
                                        (_, childIndex) => childIndex !== index
                                    ),
                                })
                            }
                        />
                    ))}
                </ul>
            )}
        </li>
    );
}

export default function AIItemTypeModal({ showModal, onHide, onSuccess }) {
    const toast = useContext(ToastContext);
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState(INITIAL_ANSWERS);
    const [tree, setTree] = useState();
    const [error, setError] = useState("");

    const [generateTree, generateLoader] = useRequest(
        SETTING_ITEMS_TYPE_AI_GENERATE,
        "POST"
    );
    const [applyTree, applyLoader] = useRequest(
        SETTING_ITEMS_TYPE_AI_APPLY,
        "POST"
    );
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    useEffect(() => {
        if (showModal) {
            setStep(0);
            setAnswers(INITIAL_ANSWERS);
            setTree(undefined);
            setError("");
        }
    }, [showModal]);

    const updateAnswer = (name, value) => {
        setAnswers({ ...answers, [name]: value });
        setError("");
    };

    const validateStep = () => {
        const requiredFields = [
            "business_description",
            "offerings",
            "customers",
            "grouping_preference",
        ];
        const field = requiredFields[step];
        if (field && !answers[field].trim()) {
            setError(t("ai.itemtype.required"));
            return false;
        }
        return true;
    };

    const nextStep = () => {
        if (validateStep()) setStep(step + 1);
    };

    const requestTree = () => {
        if (!validateStep()) return;
        confirmAndRun("item_type_tree", () => {
            generateTree(answers)
                .then((response) => {
                    setTree(response.tree);
                    setError("");
                })
                .catch((e) => {
                    if (isInsufficientCoins(e)) {
                        openFromError(e);
                        return;
                    }
                    toast(t("ai.itemtype.generate.error"), "danger");
                });
        });
    };

    const saveTree = () => {
        if (!tree?.length || hasEmptyName(tree)) {
            setError(t("ai.itemtype.tree.required"));
            return;
        }

        applyTree({ tree, max_depth: answers.max_depth })
            .then((response) => {
                toast(
                    t("ai.itemtype.save.success", {
                        count: response.created_count,
                    }),
                    "success"
                );
                onHide?.();
                onSuccess?.(response);
            })
            .catch(() => toast(t("ai.itemtype.save.error"), "danger"));
    };

    const renderQuestion = () => {
        switch (step) {
            case 0:
                return (
                    <DBFieldBlock
                        title={t("ai.itemtype.q1")}
                        required
                        error={error}
                    >
                        <DBTextArea
                            rows={4}
                            value={answers.business_description}
                            placeholder={t("ai.itemtype.q1.placeholder")}
                            onChange={(event) =>
                                updateAnswer(
                                    "business_description",
                                    event.target.value
                                )
                            }
                        />
                    </DBFieldBlock>
                );
            case 1:
                return (
                    <div className="ai-question-fields">
                        <div className="ai-step-grid">
                            <DBFieldBlock
                                title={t("ai.itemtype.offering.type")}
                            >
                                <DBSelect
                                    size="large"
                                    value={answers.offering_type}
                                    onChange={(value) =>
                                        updateAnswer("offering_type", value)
                                    }
                                    options={[
                                        {
                                            value: "product",
                                            label: t("item.type.item"),
                                        },
                                        {
                                            value: "service",
                                            label: t("item.type.service"),
                                        },
                                        {
                                            value: "mixed",
                                            label: t("ai.itemtype.mixed"),
                                        },
                                    ]}
                                />
                            </DBFieldBlock>
                        </div>
                        <DBFieldBlock
                            title={t("ai.itemtype.q2")}
                            required
                            error={error}
                        >
                            <DBTextArea
                                rows={4}
                                value={answers.offerings}
                                placeholder={t("ai.itemtype.q2.placeholder")}
                                onChange={(event) =>
                                    updateAnswer(
                                        "offerings",
                                        event.target.value
                                    )
                                }
                            />
                        </DBFieldBlock>
                    </div>
                );
            case 2:
                return (
                    <DBFieldBlock
                        title={t("ai.itemtype.q3")}
                        required
                        error={error}
                    >
                        <DBTextArea
                            rows={4}
                            value={answers.customers}
                            placeholder={t("ai.itemtype.q3.placeholder")}
                            onChange={(event) =>
                                updateAnswer("customers", event.target.value)
                            }
                        />
                    </DBFieldBlock>
                );
            case 3:
                return (
                    <div className="ai-question-fields">
                        <div className="ai-step-grid">
                            <DBFieldBlock
                                title={t("ai.itemtype.q4")}
                                required
                                error={error}
                            >
                                <DBTextArea
                                    rows={4}
                                    value={answers.grouping_preference}
                                    placeholder={t(
                                        "ai.itemtype.q4.placeholder"
                                    )}
                                    onChange={(event) =>
                                        updateAnswer(
                                            "grouping_preference",
                                            event.target.value
                                        )
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock title={t("ai.itemtype.exclusions")}>
                                <DBTextArea
                                    rows={4}
                                    value={answers.exclusions}
                                    placeholder={t(
                                        "ai.itemtype.exclusions.placeholder"
                                    )}
                                    onChange={(event) =>
                                        updateAnswer(
                                            "exclusions",
                                            event.target.value
                                        )
                                    }
                                />
                            </DBFieldBlock>
                        </div>
                    </div>
                );
            default:
                return (
                    <div className="ai-step-grid">
                        <DBFieldBlock title={t("ai.itemtype.q5")} required>
                            <DBSelect
                                size="large"
                                value={answers.max_depth}
                                onChange={(value) =>
                                    updateAnswer("max_depth", value)
                                }
                                options={[
                                    {
                                        value: 2,
                                        label: t("ai.itemtype.depth.2"),
                                    },
                                    {
                                        value: 3,
                                        label: t("ai.itemtype.depth.3"),
                                    },
                                    {
                                        value: 4,
                                        label: t("ai.itemtype.depth.4"),
                                    },
                                ]}
                            />
                        </DBFieldBlock>
                    </div>
                );
        }
    };

    return (
        <>
        <DBModal
            permanent
            show={showModal}
            minWidth={720}
            onHide={onHide}
            className="ai-item-type-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={t("ai.itemtype.title")}
            >
                <MagicStar size={24} />
            </DBModal.Header>
            <DBModal.Content>
                {!tree ? (
                    <>
                        <div className="ai-progress">
                            <span>
                                {t("ai.itemtype.step", { step: step + 1 })}
                            </span>
                            <div
                                role="progressbar"
                                aria-valuenow={step + 1}
                                aria-valuemin={1}
                                aria-valuemax={5}
                            >
                                {[0, 1, 2, 3, 4].map((item) => (
                                    <i
                                        key={item}
                                        className={item <= step ? "active" : ""}
                                    />
                                ))}
                            </div>
                        </div>
                        <div className="ai-question">{renderQuestion()}</div>
                    </>
                ) : (
                    <div className="ai-preview">
                        <h3>{t("ai.itemtype.preview.title")}</h3>
                        <p>{t("ai.itemtype.preview.description")}</p>
                        {error && <div className="ai-error">{error}</div>}
                        <ul className="ai-tree">
                            {tree.map((node, index) => (
                                <TreeNode
                                    key={index}
                                    node={node}
                                    onChange={(updatedNode) =>
                                        setTree(
                                            tree.map((item, itemIndex) =>
                                                itemIndex === index
                                                    ? updatedNode
                                                    : item
                                            )
                                        )
                                    }
                                    onDelete={() =>
                                        setTree(
                                            tree.filter(
                                                (_, itemIndex) =>
                                                    itemIndex !== index
                                            )
                                        )
                                    }
                                />
                            ))}
                        </ul>
                    </div>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                {!tree ? (
                    <>
                        {step === 4 ? (
                            <DBButton
                                caption={
                                    <>
                                        {t("ai.itemtype.generate")}
                                        <AiCoinHint operation="item_type_tree" />
                                    </>
                                }
                                onClick={requestTree}
                                loading={generateLoader}
                            />
                        ) : (
                            <DBButton
                                caption={t("ai.itemtype.next")}
                                onClick={nextStep}
                            />
                        )}
                        {step > 0 && (
                            <DBButton
                                caption={t("ai.itemtype.previous")}
                                type="secondary"
                                onClick={() => {
                                    setStep(step - 1);
                                    setError("");
                                }}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <DBButton
                            caption={t("ai.itemtype.save.tree")}
                            onClick={saveTree}
                            loading={applyLoader}
                        />
                        <DBButton
                            caption={t("ai.itemtype.regenerate")}
                            type="secondary"
                            onClick={() => {
                                setTree(undefined);
                                setStep(4);
                            }}
                        />
                    </>
                )}
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
        {gate}
        </>
    );
}
